<!-- 吐司 -->
<template>
  <transition name="toast-slide">
    <div
      v-if="visible"
      class="toast-container"
      :class="[`toast--${type}`, center ? 'toast--center' : '']"
      role="alert"
      aria-live="polite"
    >
      <span class="toast-text">{{ message }}</span>
      <div class="toast-progress-line"></div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'

// 定义 Props 类型
interface ToastProps {
  message?: string
  type?: 'info' | 'success' | 'warning' | 'error'
  duration?: number
  center?: boolean
}

// 定义默认值
const defaultProps: Required<ToastProps> = {
  message: '',
  type: 'info',
  duration: 3000,
  center: true
}

//  defineProps 并声明类型
const rawProps = defineProps<ToastProps>()

// 通过 computed 生成最终使用值
const props = {
  message: computed(() => rawProps.message ?? defaultProps.message),
  type: computed(() => rawProps.type ?? defaultProps.type),
  duration: computed(() => rawProps.duration ?? defaultProps.duration),
  center: computed(() => rawProps.center ?? defaultProps.center)
}

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'destroy'): void
}>()

const visible = ref(false)
let timer: NodeJS.Timeout | null = null

// 显示吐司
onMounted(() => {
  visible.value = true

  // 自动关闭
  if (props.duration.value > 0) {
    timer = setTimeout(() => {
      handleClose()
    }, props.duration.value)
  }
})

// 监听 duration 变化
watch(
  () => props.duration.value,
  (newDuration) => {
    if (timer) {
      clearTimeout(timer)
    }
    if (newDuration > 0 && visible.value) {
      timer = setTimeout(() => {
        handleClose()
      }, newDuration)
    }
  }
)

function handleClose() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  visible.value = false
  emit('close')

  // 等待动画完成后触发销毁
  setTimeout(() => {
    emit('destroy')
  }, 300)
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 320px;
  min-height: 32px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  box-sizing: border-box;
  border: 1px solid #ebeef5;
  max-height: 200px;
  overflow-y: auto;
}

/* 底部线条 */
.toast--info::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 285px;
  height: 2px;
  background-color: #409eff;
}

.toast--success::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 285px;
  height: 2px;
  background-color: #67c23a;
}

.toast--warning::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 285px;
  height: 2px;
  background-color: #e6a23c;
}

.toast--error::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 285px;
  height: 2px;
  background-color: #f56c6c;
}
.toast-text {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  font-weight: 500;
  text-align: center;
  word-break: break-word;
  white-space: normal;
}
.toast-progress-line {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 285px;
  height: 2px;
}

.toast--info .toast-progress-line {
  background-color: #409eff;
}

.toast--success .toast-progress-line {
  background-color: #67c23a;
}

.toast--warning .toast-progress-line {
  background-color: #e6a23c;
}

.toast--error .toast-progress-line {
  background-color: #f56c6c;
}
/* 动画效果 */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}

.toast-slide-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}
</style>
